import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Download, UsbOutlined } from '@mui/icons-material';
import {
  agentError,
  CommonKitInput,
  CommonKitStatus,
  ParkingAgentService,
} from 'services/ParkingAgentService';
import usePermissions from 'hooks/usePermissions';
import { formatBytes } from './UsbKitPanel';
import { canWriteToUsb, writeKitToUsb, UsbWriteProgress } from './usbDirectWrite';

/** 만드는 동안 상태를 다시 묻는 간격. docker save 는 수 분 단위라 촘촘할 필요가 없다. */
const POLL_MS = 5000;

/**
 * 모든 현장이 같이 쓰는 공통 설치 킷(설치 트리·도커 이미지·deb·OS 도구).
 * 현장과 무관하므로 서버 전체에서 한 벌이고, 릴리스가 바뀔 때 관리자가 다시 만든다.
 */
const CommonKitPanel: React.FC = () => {
  const { isAdmin } = usePermissions();
  const [status, setStatus] = useState<CommonKitStatus | null>(null);
  const [error, setError] = useState('');
  // 폐쇄망 현장은 apt·인터넷이 없으므로 기본으로 전부 담는다.
  const [input, setInput] = useState<CommonKitInput>({
    include_images: true,
    include_debs: true,
    include_wallpad: true,
    include_os_usb: true,
  });
  const [usbProgress, setUsbProgress] = useState<UsbWriteProgress | null>(null);
  const [usbWriting, setUsbWriting] = useState(false);
  const [usbDone, setUsbDone] = useState<{ folder: string; files: number } | null>(null);
  const [usbError, setUsbError] = useState('');
  const usbSupported = canWriteToUsb();

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      setStatus(await ParkingAgentService.commonKit(signal));
      setError('');
    } catch (e) {
      if (!signal?.aborted) setError(agentError(e));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const running = status?.status === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, refresh]);

  const build = async () => {
    setError('');
    try {
      setStatus(await ParkingAgentService.startCommonKit(input));
    } catch (e) {
      setError(agentError(e));
    }
  };

  const kit = status?.kit;

  const writeToUsb = async () => {
    setUsbError('');
    setUsbDone(null);
    setUsbProgress(null);
    setUsbWriting(true);
    try {
      const result = await writeKitToUsb(
        ParkingAgentService.commonKitDownloadUrl(),
        'luzer-kit',
        setUsbProgress,
      );
      if (result) setUsbDone(result);
    } catch (e) {
      setUsbError(e instanceof Error ? e.message : 'USB 에 쓰지 못했습니다.');
    } finally {
      setUsbWriting(false);
      setUsbProgress(null);
    }
  };

  const option = (key: keyof CommonKitInput, label: string) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={input[key]}
          disabled={running}
          onChange={(e) => setInput((prev) => ({ ...prev, [key]: e.target.checked }))}
        />
      }
      label={label}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">공통 설치 킷</Typography>
          <Typography variant="body2" color="text.secondary">
            모든 현장이 같이 쓰는 설치 프로그램·도커 이미지·설치 패키지입니다. 현장마다 다시 굽지
            않고 USB 의 luzer-kit 폴더에 한 번 담아 두면, 아래 〈현장 패키지〉만 옆에 추가해 어느
            현장이든 설치할 수 있습니다. 릴리스가 바뀌었을 때만 다시 만드세요.
          </Typography>
        </Box>

        {kit && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={kit.filename} size="small" variant="outlined" />
              <Chip
                label={`만든 시각 ${new Date(kit.built_at).toLocaleString('ko-KR')}`}
                size="small"
              />
              {kit.os_included && <Chip label="OS 무인설치 포함" size="small" color="primary" />}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              SHA256 {kit.sha256}
            </Typography>
            {kit.warnings.length > 0 && (
              <Alert severity="warning">
                <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                  {kit.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </Stack>
              </Alert>
            )}
          </Stack>
        )}

        {isAdmin ? (
          <Stack>
            {option('include_images', '도커 이미지 포함 (완전 오프라인, 수 GB)')}
            {option('include_debs', '설치 패키지(.deb) 포함 — docker·nginx·zerotier (약 250MB)')}
            {option(
              'include_wallpad',
              '월패드 지도 도구 포함 — node·크롬·생성기 (약 450MB, 콘솔의 월패드 이미지 생성에 필요)',
            )}
            {option('include_os_usb', 'OS 무인설치 USB 제작 도구 포함 (Ubuntu 24.04)')}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            공통 킷은 관리자만 다시 만들 수 있습니다.
          </Typography>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<UsbOutlined />}
              disabled={running || !status}
              onClick={() => void build()}
              sx={{ minHeight: 44 }}
            >
              {running ? '공통 킷 만드는 중…' : kit ? '공통 킷 다시 만들기' : '공통 킷 만들기'}
            </Button>
          )}
          {kit && usbSupported && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<UsbOutlined />}
              disabled={usbWriting}
              onClick={() => void writeToUsb()}
              sx={{ minHeight: 44 }}
            >
              {usbWriting ? 'USB 에 쓰는 중…' : 'USB 에 바로 쓰기'}
            </Button>
          )}
          {kit && (
            <Button
              variant="outlined"
              startIcon={<Download />}
              href={ParkingAgentService.commonKitDownloadUrl()}
              sx={{ minHeight: 44 }}
            >
              내려받기 ({formatBytes(kit.bytes)})
            </Button>
          )}
        </Stack>

        {running && (
          <Box role="status">
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              서버에서 공통 킷을 만들고 있습니다. 도커 이미지를 포함하면 수십 분 걸릴 수 있고, 그동안
              이전 킷은 그대로 받을 수 있습니다.
            </Typography>
          </Box>
        )}
        {status?.status === 'failed' && (
          <Alert severity="error">{status.error || '공통 킷을 만들지 못했습니다.'}</Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {usbError && <Alert severity="error">{usbError}</Alert>}
        {usbWriting && (
          <Box role="status">
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
              {usbProgress
                ? `USB 에 쓰는 중 — ${usbProgress.files}개 (${formatBytes(usbProgress.bytes)}) · ${usbProgress.path}`
                : 'USB 폴더를 고르면 바로 시작합니다. 창을 닫지 마세요.'}
            </Typography>
          </Box>
        )}
        {usbDone && (
          <Alert severity="success">
            <code>{usbDone.folder}</code> 에 {usbDone.files}개 파일을 썼습니다. 가져갈 현장의 현장
            패키지도 같은 USB 에 담으세요.
          </Alert>
        )}
        {status && !kit && !running && (
          <Typography color="text.secondary">아직 만든 공통 킷이 없습니다.</Typography>
        )}
      </Stack>
    </Paper>
  );
};

export default CommonKitPanel;
